// @ts-strict-ignore
import mapValues from 'lodash/mapValues';

import {Organization as ApiOrganization, Project as ApiProject, User} from 'app_api/me/types';
import {userDetailsClient} from 'common/report/queries/app-api/user-details-client';
import {toWorkspaceObj, WorkspaceObj} from 'common/report/queries/app-api/workspaces-client';
import {STAFF_DISPLAY_NAME} from 'common/constants';
import {Permission} from 'common/report/constants';
import {isStaffEmail} from 'common/report/util';

import {Store} from './store';

// getting _project_ feature flags from userDetailsStore is deprecated, use currentProjectStore instead
// feature_flags in the userDetailsStore refers to _user's_ feature flags set on their profile
export interface Project extends Omit<ApiProject, `feature_flags`> {
  id: number;
  isDemo: boolean;
  canViewProjectSettings: boolean;
}

export interface Organization extends ApiOrganization {
  isDemo: boolean;
}

export interface UserDetails {
  bio: string;
  email: string;
  pending_email: string;
  phone: string;
  id: number;
  isStaff: boolean;
  isGuest: boolean | null;
  name: string;
  login_flow: string;
  organizations: Record<string, Organization>;
  projects: Record<string, Project>;
  workspaces: Record<string, WorkspaceObj>;
  myDashboardId?: number;
  featureFlags: Set<string>;
  current_twofactor_method?: string | null;
}

export function getProjectFromResponse(project: ApiProject, projectId: string | number): Project {
  return {
    id: parseInt(projectId as string, 10),
    isDemo: project.is_demo,
    canViewProjectSettings: project.permissions && project.permissions.includes(Permission.AccessProjectSettings),
    ...project,
  };
}

const transform = (userDetails: User): UserDetails => ({
  bio: userDetails.user_bio,
  email: userDetails.user_email,
  pending_email: userDetails.user_pending_email,
  id: userDetails.user_id,
  isStaff: userDetails.is_staff,
  isGuest: userDetails.is_guest,
  name: userDetails.user_name,
  login_flow: userDetails.user_login_flow,
  phone: userDetails.user_phone,
  organizations: mapValues(userDetails.organizations, (organization) => ({
    isDemo: organization.is_demo,
    ...organization,
  })),
  projects: mapValues(userDetails.projects, getProjectFromResponse),
  workspaces: mapValues(userDetails.workspaces, (workspace) => toWorkspaceObj(workspace)),
  myDashboardId: userDetails.my_dashboard_id,
  featureFlags: new Set(userDetails.feature_flags),
  current_twofactor_method: userDetails.current_twofactor_method,
});

export const userDetailsStore = new Store<UserDetails, User>({
  apiClient: userDetailsClient,
  defaultValue: {
    bio: ``,
    email: ``,
    pending_email: null,
    login_flow: ``,
    phone: ``,
    isStaff: false,
    isGuest: null, // Assume more restrictive access while userDetails is loading
    name: ``,
    organizations: {},
    projects: {},
    workspaces: {},
    featureFlags: new Set(),
  },
  name: `user-details`,
  transform: {
    index: transform,
    update: transform,
  },
});

// users that represent Mixpanel system
export const SYSTEM_USER_IDS = [{id: 1423597, email: `eng-accounts@mixpanel.com`}];

export function isSystemUser({email = null, userId = null}: {email?: string; userId?: number}): boolean {
  return SYSTEM_USER_IDS.some((user) => user.email === email || user.id === userId);
}

export type UserDetailsStore = typeof userDetailsStore;

/**
 * Show special display name for entities created by mixpanel staff for non-staff users.
 */
export function getEntityCreatorDisplayName(creatorEmail: string, creatorName: string | null): string {
  if (isSystemUser({email: creatorEmail})) {
    return STAFF_DISPLAY_NAME;
  }

  const name = creatorName?.trim() || creatorEmail;
  if (userDetailsStore.value?.isStaff) {
    return name;
  }

  return isStaffEmail(creatorEmail) ? STAFF_DISPLAY_NAME : name;
}
